import {
    Avatar,
    Box, Button, Checkbox,
    createSvgIcon,
    Dialog, DialogContent, DialogTitle, Divider,
    Grid,
    LinearProgress, Link, Paper, Skeleton, SxProps, Table, TableBody, TableCell, TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Theme,
    Tooltip,
    Typography
} from '@mui/material';
import dayjs from 'dayjs'; // Import dayjs for date formatting
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { formatAddressObject, IsSmallScreen, toTitleCase, toUpperCase } from '../../commonFunctions/CommonFunctions';
import http from '../../commonFunctions/httpClient';
import config from '../../Config/config.json';
import { storeDataComponent } from '../../StateManagement/actions';
import store from '../../StateManagement/store';
import { FilterSearchTextNew } from '../Filter/FilterSearchTextNew';
import { WSHeaderFilter } from '../Filter/WSHeaderFilter';
import { useSnackbar } from '../Snackbar/SnackBarContext';
import { WSStatus } from '../Status/WSStatus';
import ThreeDotMenu from '../ThreeDotMenu/ThreeDotMenu';
import { WSView } from '../View/WSView';
interface DataGridProps {
    id: string;
    title?: string;
    columns: IColumns[];
    data?: Array<any>
    multiSelect?: boolean;
    headerType?: "simple" | "HaderTopCornered"
    onSelectionChange?: (selectedRows: any[]) => void;
    source?: {
        url: string;
        pageSize: number;
        page: number;
        filter: any[];
        sort: any[];
        type?: 'POST' | 'GET'
    };
    filters?: any;
    filterFields?: any
    exportCsv?: boolean,
    addButton?: any;
    transFormData?: (data: any, totalCount?: any) => any;
    onAddButtonClick?: () => void;
    buttonStyle?: 'Normal' | 'Fab',
    showpagination?: boolean;
    gridHeaderSx?: SxProps<Theme>;
    handleCheckBoxChangedValues?: (data: Array<any>) => void
    searchBox?: { fieldName?: string }
    isFilterChanged?: boolean


}

export interface IColumns {
    header: string;
    fieldName: string;
    type?: 'default' | 'link' | { image: { suffix1?: string; suffix2?: string; } } | 'action' | 'status' | 'date' | 'color' | 'img' | 'checkbox' | 'dateTime' | 'time' | 'None';
    tooltip?: boolean;
    actions?: any;
    defaultSort?: string;
    isHide?: boolean,
    isTitleCase?: boolean;
    actionCallback?: (action: string, row: any) => void;
    sortable?: boolean; // Indicate if column is sortable
    sortKey?: string
    showInLineAction?: {
        fieldName: string, valueToCheck: [{ fieldValue: any }], onClick?: (e: any, row: any) => void; actionString?: string
    },
    fieldCompare?: { fieldName: string, ValueCheck: { value: any, actionCheck: any } }
    hideAction?: {
        actionName: {
            fieldName: string,
            values: string[],
            hideAction: any[]
        }[]

    },
    hideSelectedActions?: HideSelectedActions
}
export interface HideSelectedActions {
    fieldToHide: {
        field: string;
        hideValue: string[];
        hideActions: string[];
    }[];
}
const WSDataGridNew: React.FC<DataGridProps> = ({
    id,
    columns,
    multiSelect,
    onSelectionChange,
    source,
    filters,
    filterFields,
    exportCsv = false,
    transFormData,
    title,
    addButton,
    onAddButtonClick,
    buttonStyle = "Normal",
    showpagination = true,
    gridHeaderSx,
    data,
    handleCheckBoxChangedValues,
    searchBox,
    headerType = "simple",
    isFilterChanged = false
}) => {
    const [page, setPage] = useState(0);
    const [_rows, setRows] = useState<any[]>(data || []);
    const [rowsPerPage, setRowsPerPage] = useState<number>(5);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [selectedRowsForColumn, setSelectedRowsForColumn] = useState<number[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [exporting, setExporting] = useState<boolean>(false);
    const [showImagePopup, setShowImagePopup] = useState<boolean>(false);
    const [popupImageURL, setPopupImageURL] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const gridContainerRef = useRef<HTMLDivElement>(null);
    const [gridHeight, setGridHeight] = useState<any>()
    const [newFilters, setFilters] = useState(filters);
    const { showMessage } = useSnackbar();
    const isMobile = IsSmallScreen()
    const ExportIcon = createSvgIcon(
        <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z" />,
        'SaveAlt',
    );
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
        key: '',
        direction: 'desc',
    });
    const labelFontSize = '0.75rem'
    const dispatch = useDispatch();
    const handleImageClick = (imageUrl: string) => {
        setPopupImageURL(imageUrl);
        setShowImagePopup(true);
    };

    const fetchDataFromAPI = async (
        page: number,
        pageSize: number,
        filter: any[] = [],
        sort: any[] = []
    ) => {
        try {
            setLoading(true);
            if (source?.type === 'GET') {
                const response: any = await http.get(source.url);
                return response;
            }
            if (source) {
                const response: any = await http.post(source.url, {
                    page: page,
                    pageSize: pageSize,
                    filter: filter,
                    sort: sort?.length > 0 ? sort : source?.sort?.length > 0 ? source.sort : sort,
                });
                return response;
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
        finally {
            setLoading(false);  // End loading
        }
    };

    const loadPageData = useCallback(async () => {
        try {

            const defaultSortKey = columns?.find((column: any) => column?.defaultSort);
            const sortKey: any = sortConfig.key ? sortConfig.key : defaultSortKey ? defaultSortKey.defaultSort : ''
            const sort = [{ 'field': toTitleCase(sortKey), direction: sortConfig.direction }];
            let filter: any = []
            if (source && source?.filter?.length > 0) {
                filter = [...source?.filter]
                if (newFilters) {
                    filter = [...newFilters]
                }
            }
            let response = await fetchDataFromAPI(page + 1, rowsPerPage, filter && filter?.length > 0 ? filter : newFilters, sortKey !== '' ? sort : []);
            //if (response && response.data && _rows && _rows.length > 0 && _rows[0].id !== response.data[0].id) {
            dispatch(storeDataComponent(id, response?.data || []));
            //}
            setTotalCount(response?.pagination?.totalCount | 0);
            if (transFormData) {
                setRows(transFormData(response?.data || [], response?.pagination?.totalCount));

            } else {
                setRows(response?.data || [],);
            }


        } catch (error) {
            console.error('Error loading page data:', error);
        }

    }, [page, rowsPerPage, source, id, sortConfig, newFilters, isFilterChanged, transFormData]);


    useEffect(() => {

        if (data && data?.length > 0) {

            const startIndex: any = page !== 0 ? (page) * rowsPerPage : page;
            const paginatedData: any = data.slice(startIndex, startIndex + rowsPerPage);

            setRows(paginatedData);
            //setRows(data);
            setTotalCount(data?.length)

            //  setRowsPerPage(5)
            dispatch(storeDataComponent(id, data || []));
        } else {
            loadPageData();
        }

        if (gridContainerRef.current) {
            setGridHeight(gridContainerRef.current.clientHeight);

        }
    }, [page, rowsPerPage, sortConfig, newFilters, isFilterChanged, data, source]);
    useEffect(() => {
        const unsubscribe = store.subscribe(() => {
            const state = store.getState();
            if (state.components[id]?.data === null) {
                loadPageData();
            }
        });

        return () => {
            unsubscribe();
        };
    }, [id, loadPageData]);
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value));
        setPage(0); // Reset page to 0 when rows per page changes
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelectedRows = _rows.map((_, index) => page * rowsPerPage + index);
            setSelectedRows(newSelectedRows);
            if (onSelectionChange) {
                onSelectionChange(newSelectedRows.map((idx) => _rows[idx % rowsPerPage]));
            }
            return;
        }
        setSelectedRows([]);
        if (onSelectionChange) {
            onSelectionChange([]);
        }
    };
    const handleSelectAllClickForColumns = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelectedRows = _rows.map((_, index) => page * rowsPerPage + index);
            setSelectedRowsForColumn(newSelectedRows);
            if (handleCheckBoxChangedValues) {
                handleCheckBoxChangedValues(newSelectedRows.map((idx) => _rows[idx % rowsPerPage]));
            }
            return;
        }
        setSelectedRowsForColumn([]);
        if (handleCheckBoxChangedValues) {
            handleCheckBoxChangedValues([]);
        }
    };

    const handleClick = (index: number) => {
        const selectedIndex = selectedRows.indexOf(index);

        let newSelectedRows: number[] = [];

        if (selectedIndex === -1) {
            newSelectedRows = newSelectedRows.concat(selectedRows, index);
        } else if (selectedIndex === 0) {
            newSelectedRows = newSelectedRows.concat(selectedRows.slice(1));
        } else if (selectedIndex === selectedRows?.length - 1) {
            newSelectedRows = newSelectedRows.concat(selectedRows.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelectedRows = newSelectedRows.concat(
                selectedRows.slice(0, selectedIndex),
                selectedRows.slice(selectedIndex + 1)
            );
        }
        setSelectedRows(newSelectedRows);

        if (onSelectionChange) {
            onSelectionChange(newSelectedRows.map((idx) => _rows[idx % rowsPerPage]));
        }

    };
    const handleClickForColumns = (index: number) => {

        const selectedIndexForColumn = selectedRowsForColumn.indexOf(index);

        let newSelectedRowsForColumn: number[] = [];

        if (selectedIndexForColumn === -1) {
            newSelectedRowsForColumn = newSelectedRowsForColumn.concat(selectedRowsForColumn, index);
        } else if (selectedIndexForColumn === 0) {
            newSelectedRowsForColumn = newSelectedRowsForColumn.concat(selectedRowsForColumn.slice(1));
        } else if (selectedIndexForColumn === selectedRowsForColumn?.length - 1) {
            newSelectedRowsForColumn = newSelectedRowsForColumn.concat(selectedRowsForColumn.slice(0, -1));
        } else if (selectedIndexForColumn > 0) {
            newSelectedRowsForColumn = newSelectedRowsForColumn.concat(
                selectedRowsForColumn.slice(0, selectedIndexForColumn),
                selectedRowsForColumn.slice(selectedIndexForColumn + 1)
            );
        }
        setSelectedRowsForColumn(newSelectedRowsForColumn)

        if (handleCheckBoxChangedValues) {
            handleCheckBoxChangedValues(newSelectedRowsForColumn.map((idx) => _rows[idx % rowsPerPage]));
        }
    };
    const isSelected = (index: number) => selectedRows.indexOf(index) !== -1;
    const isSelectedForColumn = (index: number) => selectedRowsForColumn.indexOf(index) !== -1;
    const handleRequestSort = (property: string) => {
        const isAsc = sortConfig.key?.toLowerCase() === property?.toLowerCase() && sortConfig.direction === 'asc';
        setSortConfig({ key: property, direction: isAsc ? 'desc' : 'asc' });
        setPage(0);
    };

    const sortedRows = () => {
        if (sortConfig.key) {
            const comparator = (a: any, b: any) => {
                if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
                if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            };
            return [..._rows].sort(comparator);
        }
        return _rows;
    };

    const isArrayOrObject = (value: any) => {
        return value && typeof value === 'object';
    };

    const handleFilterChange = (newFilters: any) => {
        if (source?.filter && source.filter.length > 0) {
            newFilters.push(...source.filter); // spread each item from source.filter into newFilters
        }
        setFilters(newFilters);
    };
    const handleAddButton = () => {
        if (onAddButtonClick) {
            onAddButtonClick();
        }
    }
    const formatExportData = (rows: any[], columns: IColumns[]) => {
        return rows.map((row) => {
            const formattedRow: any = {};
            columns?.forEach((column) => {
                if (column.fieldName.includes('addresses')) {
                    const formattedAddress: any = formatAddressObject(row[column.fieldName]);
                    formattedRow[column.fieldName] = `${formattedAddress.addressLine1 || ''} ${formattedAddress.addressLine2 || ''} ${formattedAddress.city || ''} ${formattedAddress.state || ''} ${formattedAddress.zipCode || ''}`;
                } else {
                    formattedRow[column.fieldName] = row[column.fieldName];
                }
            });
            return formattedRow;
        });
    };
    function convertTo12HourFormat(time24: string): string {
        const [hour, minute] = time24.split(":").map(Number);
        const period = hour >= 12 ? "PM" : "AM";
        const hour12 = hour % 12 === 0 ? 12 : hour % 12;
        return `${hour12}:${minute.toString().padStart(2, "0")} ${period}`;
    }

    const handleExportCSV = async () => {
        setExporting(true);
        const exportData = formatExportData(_rows, columns);
        const csvContent = [
            columns?.map((column) => column.header).join(','),
            ...exportData.map((row) =>
                columns?.map((column) => `"${row[column.fieldName] || ''}"`).join(',')
            ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${id}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setExporting(false);
    };
    const handleSearchBoxFilterChange = (updatedFilters: any[]) => {
        setFilters(updatedFilters);
    };
    return (
        <div className="data-grid-container" ref={gridContainerRef} style={{ width: '100%' }}>
            <Grid item container spacing={1} justifyContent={'start'}  >
                {/* <Grid item sm={12} xs={12} style={{ width: '100%' }}> */}
                <Paper style={{ width: '100%' }}>
                    <Grid container spacing={2} alignItems="center"  >
                        {searchBox && (
                            <Grid item xs={12} sm={3} >
                                <FilterSearchTextNew
                                    rows={{ fieldName: searchBox.fieldName }}
                                    onFilterChange={handleSearchBoxFilterChange}
                                    defaultFilter={source?.filter}
                                />
                            </Grid>
                        )}
                        {filterFields && (
                            <Grid item xs={12} sm={7} >
                                {/* <Grid container spacing={2} alignItems="center" sx={{ border: '1px solid red' }}   > */}
                                <WSHeaderFilter
                                    headerType={headerType}
                                    rows={filterFields}
                                    alignment="right"
                                    onFilterChange={handleFilterChange}
                                />
                                {/* </Grid> */}
                            </Grid>
                        )}
                        {exportCsv && <Grid item sm={2} sx={{marginTop:2}}>
                            {<Button
                                sx={{
                                    borderRadius: '999px',         // pill shape
                                    backgroundColor: '#fff!important',    // green background
                                    textTransform: 'none',         // prevent uppercase
                                    fontWeight: 500,
                                    height: '35px!important',
                                    fontSize: '0.875rem!important',
                                    paddingX: 3,
                                    color:'black',
                                    paddingY: 1,
                                    border:'1px solid #098963',
                                    '&:hover': {
                                        backgroundColor: 'whitesmoke!important',
                                          border:'1px solid #098963',
                                    },
                                }}
                                variant="outlined"
                                
                                startIcon={<ExportIcon />}
                                onClick={handleExportCSV}
                                disabled={exporting}
                            >
                                Export
                            </Button>
                            }

                        </Grid>
                        }
                    </Grid>

                    <Divider style={{ marginTop: 0 }} />

                    <Table size="small" >
                        <TableHead>
                            <TableRow component="th">
                                {multiSelect && (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={
                                                selectedRows?.length > 0 && selectedRows?.length < rowsPerPage
                                            }
                                            checked={selectedRows?.length === rowsPerPage}
                                            onChange={handleSelectAllClick}
                                            inputProps={{ 'aria-label': 'select all rows' }}
                                        />
                                    </TableCell>
                                )}

                                {columns?.map((column, index) => (
                                    !column.isHide && <TableCell className='ws-data-grid-header'
                                        key={index}
                                        sortDirection={sortConfig.key?.toLowerCase() === column.fieldName.toLowerCase() ? sortConfig.direction : false}
                                    >
                                        {column.type === 'checkbox' && (
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    indeterminate={
                                                        selectedRowsForColumn?.length > 0 && selectedRowsForColumn?.length < rowsPerPage
                                                    }
                                                    checked={selectedRowsForColumn?.length === rowsPerPage}
                                                    id={`chk-new-header${index}`}
                                                    name={`chk-new-header${index}`}
                                                    onChange={handleSelectAllClickForColumns}
                                                    inputProps={{ 'aria-label': 'select all rows' }}
                                                />
                                            </TableCell>
                                        )}
                                        {(column.sortable && !column.isHide) ? (
                                            <TableSortLabel
                                                active={sortConfig.key?.toLowerCase() === column.fieldName.toLowerCase()}
                                                direction={sortConfig.key?.toLowerCase() === column.fieldName.toLowerCase() ? sortConfig.direction : 'asc'}
                                                onClick={() => handleRequestSort(column.sortKey ? column.sortKey : column.fieldName)}
                                            >
                                                {toUpperCase(column.header)}
                                            </TableSortLabel>
                                        ) : (
                                            <Typography sx={{ fontSize: labelFontSize, fontWeight: 'bold' }}>{toUpperCase(column.header)}</Typography>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        {sortedRows()?.length > 0 ? (<TableBody>
                            {loading ? (
                                <>
                                    {/* Progress bar at the top */}
                                    {/* <Grid
                                    display="block"
                                    width="100%"
                                    justifyContent="center"
                                    alignItems="center"
                                    minHeight={400}
                                    
                                >
                                    <LinearProgress />
                                </Grid> */}

                                    {/* Skeleton and loading indication */}
                                    <TableRow component="th" >
                                        <TableCell
                                            colSpan={columns?.length + (multiSelect ? 1 : 0)}
                                            align="center"
                                        >
                                            <Box
                                                display="flex"
                                                width="100%"
                                                justifyContent="center"
                                                alignItems="center"
                                                minHeight={gridHeight - 200} // Adjust the height based on your grid size
                                            >
                                                <Grid
                                                    container
                                                    spacing={2}
                                                    width="90%"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                >
                                                    <Grid item sm={12}>
                                                        {/* Skeleton loader as a placeholder */}
                                                        <Skeleton width="100%">
                                                            <Typography>.</Typography>
                                                        </Skeleton>

                                                        {/* Rectangular skeleton */}
                                                        <Skeleton
                                                            animation="pulse"
                                                            variant="rectangular"
                                                            width="100%"
                                                        >
                                                            <div style={{ paddingTop: '17%' }} />
                                                        </Skeleton>

                                                        <Skeleton width="100%">
                                                            <Typography>.</Typography>
                                                        </Skeleton>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </>
                            )
                                : (sortedRows().map((row: any, index: number) => {
                                    const isItemSelected = isSelected(page * rowsPerPage + index);
                                    const isItemSelectedForColumn = isSelectedForColumn(page * rowsPerPage + index)
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    const labelIdisItemSelectedForColumn = `enhanced-table-checkbox-isItemSelectedForColumn${index}`;
                                    return (
                                        <TableRow sx={{ overflow: 'auto' }}
                                            hover

                                            role="checkbox"
                                            aria-checked={isItemSelected}
                                            tabIndex={-1}
                                            key={row.id}
                                            selected={isItemSelected}
                                        >
                                            {multiSelect && (
                                                <TableCell padding="checkbox">
                                                    <Checkbox id={`chk-sel${index}`}
                                                        onClick={() => handleClick(page * rowsPerPage + index)}
                                                        checked={isItemSelected}
                                                        inputProps={{ 'aria-labelledby': labelId }}
                                                    />
                                                </TableCell>
                                            )}
                                            {columns?.map((column, colIndex) => {

                                                if (column.type === 'default' && !column?.isHide && column.tooltip === true) {
                                                    return (<TableCell key={colIndex}>
                                                        <WSView style={{ fontSize: '0.75rem' }} dotStartFrom={18}
                                                            title={column.isTitleCase ? toTitleCase(row[column.fieldName]) : row[column.fieldName]}
                                                            isTooltip={column.tooltip}
                                                        />
                                                    </TableCell>);
                                                } else if (column.type === 'link' && !column?.isHide) {
                                                    return (
                                                        <TableCell key={colIndex} >
                                                            <Link sx={{ fontSize: labelFontSize }} href={row[column.fieldName]} target="_blank" rel="noopener noreferrer">
                                                                {row[column.fieldName]}
                                                            </Link>
                                                        </TableCell>
                                                    );
                                                } else if (typeof column.type === 'object' && column.type.image && !column?.isHide) {
                                                    return (
                                                        <TableCell key={colIndex}>
                                                            <Grid item container spacing={3}>
                                                                <Grid item sm={4}>
                                                                    <img
                                                                        src={row[column.fieldName]}
                                                                        alt="thumbnail"
                                                                        onClick={() => handleImageClick(row[column.fieldName])}
                                                                        style={{ cursor: 'pointer', maxWidth: '100px', height: '40px' }}
                                                                    />
                                                                </Grid>
                                                                {column.type.image.suffix1 && <Grid item sm={4}>
                                                                    <Typography style={{ fontSize: '1rem', paddingTop: !column.type?.image?.suffix2 ? '10px' : '0' }}>{row[column.type.image.suffix1]}</Typography>
                                                                    {column.type?.image?.suffix2 && <Typography style={{ fontSize: '0.0.75rem' }}>{row[column.type?.image?.suffix2]}</Typography>}
                                                                </Grid>}
                                                            </Grid>


                                                        </TableCell>
                                                    );
                                                } else if (column.type === 'status' && !column?.isHide) {
                                                    return (
                                                        <TableCell key={colIndex}>
                                                            <WSStatus style={{ fontSize: labelFontSize }} value={row[column.fieldName]} />

                                                            {column.showInLineAction?.valueToCheck?.some((item: any) =>
                                                                item.fieldValue?.toLowerCase?.() === row[column.fieldName]?.toLowerCase?.()
                                                            ) && (
                                                                    <Link
                                                                        component="button"
                                                                        underline="hover"

                                                                        sx={{ ml: 1, cursor: 'pointer', fontSize: '0.75rem' }} // Optional: adds spacing between WSStatus and link
                                                                        onClick={(e) => column.showInLineAction?.onClick?.(e, row)}
                                                                    >
                                                                        {column.showInLineAction?.actionString || 'Create'}
                                                                    </Link>
                                                                )}
                                                        </TableCell>

                                                    );
                                                }

                                                else if (column.type === 'checkbox' && !column?.isHide) {
                                                    return (
                                                        <TableCell key={colIndex}>

                                                            <Checkbox id={`chkcolumn-${index}`} name={`chk-${index}`}
                                                                checked={isItemSelectedForColumn}
                                                                onClick={() => handleClickForColumns(page * rowsPerPage + index)}
                                                                inputProps={{ 'aria-labelledby': labelIdisItemSelectedForColumn }}
                                                            />
                                                        </TableCell>
                                                    );
                                                }
                                                else if (column.type === 'img' && !column?.isHide) {
                                                    return (
                                                        <TableCell key={colIndex}>
                                                            <Grid item container spacing={3}>
                                                                <Grid item sm={4}>
                                                                    <Tooltip title={<>  <img
                                                                        src={`${config.imgUrl}${row[column.fieldName]}`}
                                                                        alt="thumbnail"

                                                                        style={{ cursor: 'pointer', width: '200px', height: '150px' }}
                                                                    /></>}>
                                                                        <img
                                                                            src={`${config.imgUrl}${row[column.fieldName]}`}
                                                                            alt="thumbnail"
                                                                            onClick={() => window.open(`${config.imgUrl}${row[column.fieldName]}`, '_blank')}
                                                                            style={{ cursor: 'pointer', width: '50px', height: '40px' }}
                                                                        />
                                                                    </Tooltip>
                                                                </Grid>
                                                            </Grid>
                                                        </TableCell>
                                                    );
                                                }
                                                else if (column.type === 'color' && !column?.isHide) {
                                                    return (
                                                        <TableCell key={colIndex}>
                                                            <Box display="flex" alignItems="center" >

                                                                <Avatar
                                                                    sx={{ bgcolor: row[column.fieldName] ? row[column.fieldName] : 'gray', width: 24, height: 24, marginRight: 1, fontSize: '0.75rem', }}
                                                                    variant="rounded" defaultValue={''}
                                                                ><></></Avatar>
                                                                <Typography sx={{ fontSize: labelFontSize }} onDoubleClick={() => {
                                                                    const textToCopy = toTitleCase(row[column.fieldName] || '');
                                                                    navigator.clipboard.writeText(textToCopy).then(() => {
                                                                        showMessage(`Copied: ${textToCopy}`, 'success');
                                                                    });
                                                                }}>{toTitleCase(row[column.fieldName])}</Typography>
                                                            </Box>
                                                        </TableCell>
                                                    );
                                                }

                                                else if (column.type === 'date' && !column?.isHide) {
                                                    return (
                                                        <TableCell key={colIndex}>
                                                            <Typography style={{ fontSize: labelFontSize }}>
                                                                {dayjs(row[column.fieldName]).isValid() ? dayjs(row[column.fieldName]).format('DD/MM/YYYY') : 'Invalid Date'}
                                                            </Typography>
                                                        </TableCell>
                                                    );
                                                }
                                                else if (column.type === 'dateTime' && !column?.isHide) {
                                                    return (
                                                        <TableCell key={colIndex}>
                                                            <Typography style={{ fontSize: labelFontSize }}>
                                                                {dayjs(row[column.fieldName]).isValid() ? dayjs(row[column.fieldName]).format('DD/MM/YYYY hh:mm') : 'Invalid Date'}
                                                            </Typography>
                                                        </TableCell>
                                                    );
                                                }
                                                else if (column.type === 'time' && !column?.isHide) {
                                                    return (
                                                        <TableCell key={colIndex}>
                                                            <Typography style={{ fontSize: labelFontSize }}>
                                                                {convertTo12HourFormat(row[column.fieldName])}
                                                            </Typography>
                                                        </TableCell>
                                                    );
                                                }
                                                // else if (column.type === 'date') {
                                                //     return (
                                                //         <TableCell key={colIndex}>
                                                //             <Typography  style={{fontSize:'0.875rem'}} >{dayjs(row[column.fieldName])?.toDate().toLocaleDateString()}</Typography>
                                                //         </TableCell>
                                                //     );
                                                // }
                                                else if (column.type === 'action' && column?.actions?.length > 0) {
                                                    return (
                                                        <TableCell key={colIndex}>
                                                            <ThreeDotMenu
                                                                align='vertical'
                                                                field={column?.fieldCompare}
                                                                items={column.actions}
                                                                value={column?.fieldCompare ? row[column?.fieldCompare.fieldName] : undefined}
                                                                rows={row}
                                                                hideSelectedActions={column?.hideSelectedActions}
                                                                onAction={(action: string) =>
                                                                    column.actionCallback?.(action, row)
                                                                }
                                                                hideAction={column.hideAction}
                                                            />
                                                        </TableCell>
                                                    );
                                                }
                                                else if (!column?.isHide) {
                                                    return (
                                                        <TableCell key={colIndex} >
                                                            <Typography sx={{ fontSize: labelFontSize }}>{isArrayOrObject(row[column.fieldName]) ? JSON.stringify(column.isTitleCase ? toTitleCase(row[column.fieldName]) : row[column.fieldName]) : column.isTitleCase ? toTitleCase(row[column.fieldName]) : row[column.fieldName]}</Typography>
                                                        </TableCell>
                                                    );
                                                }
                                            })}
                                        </TableRow>
                                    );
                                }))}
                        </TableBody>) : loading ? (
                            <>
                                <TableBody>
                                    <TableRow>
                                        <TableCell colSpan={columns?.length + (multiSelect ? 1 : 0)} align="center">
                                            <Box
                                                display="block"
                                                width="100%"
                                                justifyContent="center"
                                                alignItems="center"
                                                minHeight={400}
                                            >
                                                <LinearProgress />
                                            </Box>

                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                                {/* <Box
                                    display="block"
                                    width="100%"
                                    justifyContent="center"
                                    alignItems="center"
                                    minHeight={400}
                                >
                                    <LinearProgress />
                                </Box> */}

                                {/* Skeleton and loading indication */}

                            </>
                        ) : (
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={columns?.length + (multiSelect ? 1 : 0)} align="center">
                                        <Typography variant="h6" style={{ padding: '20px', fontSize: '1.15rem' }}>
                                            No Records to Show
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )}
                    </Table>
                    {/* )} */}
                    {showpagination === true && <TablePagination sx={{ fontSize: labelFontSize }}
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />}
                </Paper>
                {/* </Grid> */}
            </Grid>
            <Dialog open={showImagePopup} onClose={() => setShowImagePopup(false)} maxWidth="xs">
                <DialogTitle>Image</DialogTitle>
                <DialogContent>
                    <img src={popupImageURL} alt="Large Preview" style={{ maxWidth: '100%' }} />
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default WSDataGridNew;
